import React from "react";
import { formatDate } from "./blogFirebase";

/**
 * Hero image, article header, rendered markdown body, and secondary (mid-article) image.
 */
const BlogPostBody = ({
  post,
  contentHtml,
  showImgPrompt,
  onToggleImgPrompt,
  showSecondaryImgPrompt,
  onToggleSecondaryImgPrompt,
}) => (
  <div className="max-w-[860px] mx-auto px-4 py-8">
    {/* Hero + Mid-article images side by side */}
    <div className="grid grid-cols-2 gap-4 mb-2">
      {/* Hero image */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Hero image</p>
        {post.image ? (
          <div className="relative mb-1">
            <img
              src={post.image}
              alt={post.title}
              className="w-full rounded-xl object-cover"
              style={{ maxHeight: "280px" }}
            />
            {post.imagePrompt && (
              <button
                onClick={onToggleImgPrompt}
                className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full hover:bg-black/70"
              >
                {showImgPrompt ? "✕ Hide prompt" : "🔍 Image prompt"}
              </button>
            )}
          </div>
        ) : (
          <div className="w-full h-40 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
            <span className="text-5xl">{post.emoji || "🎂"}</span>
          </div>
        )}
        {showImgPrompt && post.imagePrompt && (
          <div className="mb-2 px-3 py-2 bg-gray-900 text-gray-200 text-xs leading-relaxed font-mono rounded-b-xl border-t border-gray-700 break-words whitespace-pre-wrap">
            <span className="text-gray-500 uppercase tracking-wide text-[10px] block mb-1">Image generation prompt</span>
            {post.imagePrompt}
          </div>
        )}
      </div>

      {/* Mid-article image */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Mid-article image</p>
        {post.secondaryImage ? (
          <div className="relative mb-1">
            <img
              src={post.secondaryImage}
              alt={`${post.title} — secondary`}
              className="w-full rounded-xl object-cover"
              style={{ maxHeight: "280px" }}
            />
            {post.secondaryImagePrompt && (
              <button
                onClick={onToggleSecondaryImgPrompt}
                className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full hover:bg-black/70"
              >
                {showSecondaryImgPrompt ? "✕ Hide prompt" : "🔍 Image prompt"}
              </button>
            )}
          </div>
        ) : (
          <div className="w-full h-40 bg-gray-100 rounded-xl flex items-center justify-center mb-1">
            <span className="text-gray-400 text-sm">No mid-article image yet</span>
          </div>
        )}
        {showSecondaryImgPrompt && post.secondaryImagePrompt && (
          <div className="mb-2 px-3 py-2 bg-gray-900 text-gray-200 text-xs leading-relaxed font-mono rounded-b-xl border-t border-gray-700 break-words whitespace-pre-wrap">
            <span className="text-gray-500 uppercase tracking-wide text-[10px] block mb-1">Secondary image generation prompt</span>
            {post.secondaryImagePrompt}
          </div>
        )}
      </div>
    </div>

    {/* Article header */}
    <div className="max-w-[740px] mx-auto mt-6 mb-6">
      <div className="flex flex-wrap gap-2 mb-3">
        {(post.tags || []).slice(0, 3).map(tag => (
          <span key={tag} className="px-3 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{tag}</span>
        ))}
      </div>

      <h1 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1.875rem", lineHeight: 1.25, color: "#1a1a2e", marginBottom: "0.75rem" }}>
        {post.emoji && <span className="mr-2">{post.emoji}</span>}{post.title}
      </h1>

      <p style={{ color: "#555", fontSize: "1.0625rem", marginBottom: "0.75rem", lineHeight: 1.6 }}>
        {post.description}
      </p>

      <div className="flex gap-4 text-sm text-gray-400 mb-8">
        <span>{formatDate(post.date)}</span>
        <span>·</span>
        <span>{post.readTime || "5 min read"}</span>
        <span>·</span>
        <span className="text-gray-300">/{post.slug}</span>
      </div>

      <hr style={{ borderTop: "1px solid #e5e7eb", marginBottom: "2rem" }} />

      {/* Rendered markdown */}
      <div
        className="prose-blog"
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
    </div>

  </div>
);

export default BlogPostBody;
